import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const Ctx = createContext(null);
const USER_THEME_KEY = "ovamx:user_theme";
const VALID_THEMES = ["arctic", "slate", "ivory", "ember", "forest", "sakura", "midnight", "sepia"];

const FALLBACK = {
  theme: "arctic",
  accent_color: "#E07A5F",
  accent_color_hover: "#C96A51",
  brand: { name: "Ovamx", tagline: "Tu tiempo tiene valor" },
  landing: { hero: {}, manifesto: { items: [] }, stats: [], marquee: [], sections_visible: {} },
  typography: { font_heading: "Playfair Display", font_body: "Inter", font_size_base: 16, font_size_h1_scale: 1, letter_spacing: 0, bold_titles: true },
  style: { mode: "light", secondary_color: "#3D5A80", border_radius_scale: 1, button_size: "md", shadow_intensity: "soft", design_style: "modern" },
  home_modules: [],
};

function readUserTheme() {
  try {
    const v = localStorage.getItem(USER_THEME_KEY);
    return VALID_THEMES.includes(v) ? v : null;
  } catch { return null; }
}

const FONT_LINK_ID = "ovamx-google-fonts";

function loadGoogleFonts(fonts) {
  const families = Array.from(new Set(fonts.filter(Boolean)))
    .map((f) => `family=${encodeURIComponent(f)}:wght@400;500;600;700`)
    .join("&");
  if (!families) return;
  const href = `https://fonts.googleapis.com/css2?${families}&display=swap`;
  let link = document.getElementById(FONT_LINK_ID);
  if (!link) {
    link = document.createElement("link");
    link.id = FONT_LINK_ID;
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  if (link.href !== href) link.href = href;
}

function applyVisuals(cfg, { honorUserTheme = true } = {}) {
  if (!cfg) return;
  const root = document.documentElement;
  const userTheme = honorUserTheme ? readUserTheme() : null;
  const themeToApply = userTheme || cfg.theme || "arctic";
  if (themeToApply) root.setAttribute("data-theme", themeToApply);

  const style = cfg.style || {};
  if (style.mode) root.setAttribute("data-mode", style.mode);
  if (style.design_style) root.setAttribute("data-design", style.design_style);

  // Accent + secondary colors
  if (cfg.accent_color) root.style.setProperty("--coral-dynamic", cfg.accent_color);
  if (cfg.accent_color_hover) root.style.setProperty("--coral-hover-dynamic", cfg.accent_color_hover);
  if (style.secondary_color) root.style.setProperty("--secondary-dynamic", style.secondary_color);

  // Typography
  const typo = cfg.typography || {};
  loadGoogleFonts([typo.font_heading, typo.font_body]);
  if (typo.font_heading) root.style.setProperty("--font-heading", `"${typo.font_heading}", Georgia, serif`);
  if (typo.font_body) root.style.setProperty("--font-body", `"${typo.font_body}", system-ui, sans-serif`);
  if (typo.font_size_base) root.style.setProperty("--font-size-base", `${typo.font_size_base}px`);
  if (typo.font_size_h1_scale) root.style.setProperty("--h1-scale", typo.font_size_h1_scale);
  if (typo.letter_spacing !== undefined && typo.letter_spacing !== null) {
    root.style.setProperty("--letter-spacing", `${typo.letter_spacing / 100}em`);
  }
  root.style.setProperty("--title-weight", typo.bold_titles ? "600" : "400");

  // Border radius scale
  const rs = style.border_radius_scale ?? 1;
  root.style.setProperty("--radius-scale", String(rs));

  // Button size
  const btnSize = style.button_size === "sm" ? "0.85" : style.button_size === "lg" ? "1.15" : "1";
  root.style.setProperty("--button-scale", btnSize);

  // Shadow intensity
  const shadow = style.shadow_intensity === "none" ? "none"
    : style.shadow_intensity === "strong" ? "0 12px 40px rgba(14,22,38,0.18)"
    : "0 4px 16px rgba(14,22,38,0.08)";
  root.style.setProperty("--shadow-default", shadow);

  // Build coral utility CSS once per change
  const styleId = "ovamx-dynamic-accent";
  let styleEl = document.getElementById(styleId);
  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.id = styleId;
    document.head.appendChild(styleEl);
  }
  if (cfg.accent_color) {
    styleEl.textContent = `
      .text-coral { color: ${cfg.accent_color} !important; }
      .bg-coral { background-color: ${cfg.accent_color} !important; }
      .border-coral { border-color: ${cfg.accent_color} !important; }
      .hover\\:text-coral:hover { color: ${cfg.accent_color} !important; }
      .hover\\:bg-coral:hover { background-color: ${cfg.accent_color} !important; }
      .hover\\:border-coral:hover { border-color: ${cfg.accent_color} !important; }
      .bg-coral-hover { background-color: ${cfg.accent_color_hover || cfg.accent_color} !important; }
      .hover\\:bg-coral-hover:hover { background-color: ${cfg.accent_color_hover || cfg.accent_color} !important; }
      :root { --coral: ${cfg.accent_color}; --coral-hover: ${cfg.accent_color_hover || cfg.accent_color}; }
      ${typo.font_body ? `body { font-family: "${typo.font_body}", system-ui, sans-serif; }` : ""}
      ${typo.font_heading ? `.font-serif, h1, h2, h3 { font-family: "${typo.font_heading}", Georgia, serif; }` : ""}
    `;
  }

  // Dark mode: applied via data-mode on <html>; CSS variables defined in index.css
}

export function SiteConfigProvider({ children }) {
  const [config, setConfig] = useState(FALLBACK);
  const [loaded, setLoaded] = useState(false);
  const [userTheme, setUserThemeState] = useState(readUserTheme());

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/site-config`);
      setConfig(data);
      applyVisuals(data);
    } catch {
      applyVisuals(FALLBACK);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setUserTheme = (id) => {
    if (!VALID_THEMES.includes(id)) return;
    try { localStorage.setItem(USER_THEME_KEY, id); } catch {}
    setUserThemeState(id);
    document.documentElement.setAttribute("data-theme", id);
  };

  const clearUserTheme = () => {
    try { localStorage.removeItem(USER_THEME_KEY); } catch {}
    setUserThemeState(null);
    document.documentElement.setAttribute("data-theme", config?.theme || "arctic");
  };

  return (
    <Ctx.Provider value={{ config, loaded, reload: load, applyVisuals, userTheme, setUserTheme, clearUserTheme }}>
      {children}
    </Ctx.Provider>
  );
}

export const useSiteConfig = () => {
  const ctx = useContext(Ctx);
  if (!ctx) return { config: FALLBACK, loaded: false, reload: () => {}, applyVisuals: () => {}, userTheme: null, setUserTheme: () => {}, clearUserTheme: () => {} };
  return ctx;
};
